import PropTypes from "prop-types";
import React from "react";

import { SMALL, useDeviceInfo } from "@swa-ui/browser";
import { ChaseFirmOfferConfirmation, UrgencyTriggerBadge } from "@swa-ui/chase";
import { Badge, Card, Icon, Image } from "@swa-ui/core";
import { applyOverrides, CallToAction } from "@swa-ui/placements";
import { TextContent } from "@swa-ui/placements";
import { classNames } from "@swa-ui/string";

import { SPECIAL_OFFER_1, SPECIAL_OFFERS } from "../../defines/layoutType";
import { getStyleProps, renderOfferDetails } from "../OfferUtils";
import styles from "./SpecialOffersPlacement.module.scss";

export const SpecialOffersPlacement = (props) => {
  const { additionalProps, placement, placementData } = props;
  const {
    backgroundColor,
    badges,
    callToAction,
    command,
    commandProps,
    disclaimer,
    eyebrow,
    graphic,
    offerDetails,
    text,
  } = placement ?? {};

  const { primaryText, secondaryText, callout } = text ?? {};
  const { layoutType } = additionalProps ?? {};
  const { screenSize } = useDeviceInfo();
  const { isChasePlacement = false, isChasePrequal = false } = placementData || {};

  return isChasePlacement && isChasePrequal ? (
    <ChaseFirmOfferConfirmation>{renderPlacementContent()}</ChaseFirmOfferConfirmation>
  ) : (
    renderPlacementContent()
  );

  function renderPlacementContent() {
    return (
      <Card {...getCardProps()}>
        <div className={getClass()} style={{ backgroundColor }}>
          {graphic?.imagePath && renderGraphicImagePath()}
          {badges && renderSpecialOfferTags()}
          {layoutType === SPECIAL_OFFER_1
            ? renderSpecialOfferOneContent()
            : renderSpecialOffersContent()}
        </div>
      </Card>
    );
  }

  function renderSpecialOfferOneContent() {
    return (
      <div className={styles.specialOfferContentContainer}>
        {renderUrgencyTriggerBadge() || renderEyebrow()}
        {renderCalloutText()}
        {renderPrimaryText()}
        {offerDetails && (
          <div className={styles.offerDetails}>{renderOfferDetails(offerDetails, styles)}</div>
        )}
        {renderSecondaryText()}
        {renderDisclaimer()}
        {callToAction && renderCallToAction()}
      </div>
    );
  }

  function renderSpecialOffersContent() {
    return (
      <div className={styles.contentContainer}>
        <div>
          {renderUrgencyTriggerBadge() || renderEyebrow()}
          {renderCalloutText()}
          {renderPrimaryText()}
          {offerDetails && (
            <div className={styles.offerDetails}>{renderOfferDetails(offerDetails, styles)}</div>
          )}
        </div>
        <div className={styles.secondaryContentContainer}>
          {renderSecondaryText()}
          {renderDisclaimer()}
        </div>
        {callToAction && renderCallToAction()}
      </div>
    );
  }

  function renderGraphicImagePath() {
    const { altText, height, imagePath, width } = applyOverrides(graphic, screenSize);

    return (
      <Image
        alt={altText}
        className={getImagePathClass()}
        fitToContainer
        height={height}
        src={imagePath}
        width={width}
      />
    );
  }

  function renderSpecialOfferTags() {
    return <div className={styles.specialOfferTagContainer}>{renderBadge()}</div>;
  }

  function renderBadge() {
    return badges.map((badge) => (
      <Badge key={badge.value} {...getBadgeProps(badge, "specialOfferTag", "badge")} />
    ));
  }

  function renderEyebrow() {
    return eyebrow && <Badge {...getBadgeProps(eyebrow, "eyebrowBadge", "eyebrowText")} />;
  }

  function renderPrimaryText() {
    return primaryText?.value && <TextContent {...primaryText} />;
  }

  function renderUrgencyTriggerBadge() {
    return (
      command === "URGENCY_TRIGGER" && (
        <div className={classNames(styles.urgencyTriggerBadgeContent)}>
          <UrgencyTriggerBadge commandProps={commandProps} />
        </div>
      )
    );
  }

  function renderSecondaryText() {
    return secondaryText?.value && <TextContent {...secondaryText} />;
  }

  function renderCalloutText() {
    return callout && <TextContent {...callout} />;
  }

  function renderDisclaimer() {
    return disclaimer?.value && <TextContent {...disclaimer} />;
  }

  function renderCallToAction() {
    return (
      <div className={styles.callToAction}>
        <CallToAction {...getCallToActionProps()} />
      </div>
    );
  }

  function getCardProps() {
    return {
      className: styles.cardContainer,
      fullHeight: layoutType !== SPECIAL_OFFER_1,
      fullHeightContent: true,
      fullWidthContent: true,
    };
  }

  function getBadgeProps(badge, className, childClass) {
    const { badgeStyle, badgeType, foregroundColor, prefixIcon, suffixIcon, value } = badge;

    return {
      children: (
        <span className={styles[childClass]} {...getStyleProps(badge)}>
          {prefixIcon && <Icon {...getIconProps(prefixIcon)} />}
          <TextContent value={value} />
          {suffixIcon && <Icon {...getIconProps(suffixIcon)} />}
        </span>
      ),
      className: styles[className],
      foregroundColor,
      padding: "xsmall",
      styleType: badgeStyle,
      type: badgeType,
    };
  }

  function getCallToActionProps() {
    return {
      paddingForFocus: false,
      ...(screenSize === SMALL && { additionalProps: { fullWidth: true } }),
      ...callToAction,
    };
  }

  function getIconProps(icon) {
    const { color, name } = icon;

    return {
      className: styles.eyebrowIcon,
      color,
      name,
    };
  }

  function getClass() {
    return classNames({
      [styles.specialOfferContainer]: layoutType === SPECIAL_OFFERS,
      [styles.specialOfferContainer1]: layoutType === SPECIAL_OFFER_1,
    });
  }

  function getImagePathClass() {
    return classNames({
      [styles.specialOfferImage]: layoutType === SPECIAL_OFFERS,
      [styles.specialOfferImage1]: layoutType === SPECIAL_OFFER_1,
    });
  }
};

SpecialOffersPlacement.displayName = "SpecialOffersPlacement";
SpecialOffersPlacement.propTypes = {
  /** Additional props to position the component. */
  additionalProps: PropTypes.object,

  /** Object that contains placement props, including backgroundColor, callToAction, etc */
  placement: PropTypes.shape({
    /** String that contains background color for the particular placement. */
    backgroundColor: PropTypes.string,

    /** Object that contains badge props. */
    badges: PropTypes.array,

    /** Object that contains link/button props. */
    callToAction: PropTypes.object,

    /** Object that contains legal disclaimer text props. */
    disclaimer: PropTypes.shape({
      value: PropTypes.string,
    }),

    /** Object that contains label props. */
    eyebrow: PropTypes.object,

    /** Object that contains image props. */
    graphic: PropTypes.shape({
      altText: PropTypes.string,
      imagePath: PropTypes.string,
      overrides: PropTypes.shape({
        large: PropTypes.shape({
          altText: PropTypes.string,
          imagePath: PropTypes.string,
        }),
        medium: PropTypes.shape({
          altText: PropTypes.string,
          imagePath: PropTypes.string,
        }),
        small: PropTypes.shape({
          altText: PropTypes.string,
          imagePath: PropTypes.string,
        }),
      }),
    }),

    offerDetails: PropTypes.shape({
      delimiter: PropTypes.shape({
        lineSeparator: PropTypes.shape({
          color: PropTypes.string,
          fontWeight: PropTypes.string,
        }),
        text: PropTypes.shape({
          color: PropTypes.string,
          fontSize: PropTypes.string,
          value: PropTypes.string,
        }),
      }),
      offers: PropTypes.arrayOf(
        PropTypes.shape({
          bottomCaption: PropTypes.shape({
            color: PropTypes.string,
            fontSize: PropTypes.string,
            value: PropTypes.string,
          }),
          leadingText: PropTypes.object,
          topCaption: PropTypes.shape({
            color: PropTypes.string,
            fontSize: PropTypes.string,
            value: PropTypes.string,
          }),
          trailingText: PropTypes.object,
          value: PropTypes.object,
        })
      ),
    }),

    /** Object that contains primary text/secondary text/label props. */
    text: PropTypes.shape({
      callout: PropTypes.object,
      label: PropTypes.shape({
        color: PropTypes.string,
        fontSize: PropTypes.string,
        fontWeight: PropTypes.string,
        value: PropTypes.string,
      }),
      primaryText: PropTypes.shape({
        color: PropTypes.string,
        fontSize: PropTypes.string,
        fontWeight: PropTypes.string,
        value: PropTypes.string,
      }),
      secondaryText: PropTypes.object,
    }),
  }),

  /** Object containing WCM configured placement type data. */
  placementData: PropTypes.object,
};
